import AgoraRTC, {
  IAgoraRTCRemoteUser,
  IRemoteAudioTrack,
  IRemoteVideoTrack,
  NetworkQuality,
  UID,
} from "agora-rtc-sdk-ng";
import axios from "axios";
import { useCallback, useEffect, useMemo } from "react";
// import { endRecording, startRecording } from "../api/apiCalls";

const AGORA_APP_ID = "29f9a0155f564d3189fe0c5755f582c4";
const AGORA_SIGN_KEY = "eb5c659478674c8ca7b5d5195c3879a9";
const _BASE_SERVER = "conv.sandbox.emotionagents.com";
const tokenizerApi = `https://${_BASE_SERVER}/Tokenizer/gen`;

const LogLevel: 0 | 1 | 3 | 2 | 4 = 4;

AgoraRTC.setLogLevel(LogLevel);

export const getToken = async (channelName: string) => {
  console.debug("channel name", channelName);

  try {
    const request = {
      uid: "0",
      role: "no-role",
      expireTime: 180,
      channelName: channelName,
      appId: AGORA_APP_ID,
      sign_key: AGORA_SIGN_KEY,
    };
    // console.debug("request", request);
    const resp = await axios.post(tokenizerApi, request, {
      headers: {},
    });

    return resp.data["token"];
  } catch (error) {
    console.debug(error);
    return "";
  }
};
export const useAgora = (
  channel: string,
  onVolumeUpdate?: (uid: number | string, level: number) => any,
  onCoreLeft?: () => void | Promise<void>,
  onRemoteJoined?: (
    remoteUser: IAgoraRTCRemoteUser,
    frameDecode: boolean
  ) => Promise<void> | void
) => {
  const client = useMemo(
    () => AgoraRTC.createClient({ codec: "vp8", mode: "rtc" }),
    []
  );

  const onRemoteUserPublished = useCallback(
    async (remoteUser: IAgoraRTCRemoteUser, mediaType: "audio" | "video") => {
      const user = client.remoteUsers.find((user) => {
        const isSameExists = user.uid === remoteUser.uid;
        // will be true if the received media type is audio and the remote user published
        // And currently we are receiving "audio"
        const isAudioMediaTypeSubscribed =
          mediaType === "audio" && user.hasAudio;
        // will be true if the received media type is audio and the remote user published
        // And currently we are receiving "video"
        const isVideoTypeSubscribed = mediaType === "video" && user.hasVideo;
        return (
          isSameExists && (isVideoTypeSubscribed || isAudioMediaTypeSubscribed)
        );
      });
      if (!user) return;
      const track = await client.subscribe(remoteUser, mediaType);
      if (mediaType === "video" && remoteUser.uid !== client.uid) {
        const userVideoTrack = track as IRemoteVideoTrack;

        if (userVideoTrack) {
          const elem = document.getElementById("VideoConteiner") as HTMLElement; // reassign the videoConteinerElemnt to short version
          userVideoTrack.play(elem, { mirror: false, fit: "fill" });
          userVideoTrack.on("first-frame-decoded", () => {
            /*
             * Here we set styles for agora video stram
             * We choose to play the video on inside videoConteinerElemnt (aka elem)
             * The first child of videoContainer is the div where the video reside
             * The div first and only child is the video tag it self
             * We overlay on the same DIV!!! where the video is
             */
            try {
              const res = onRemoteJoined && onRemoteJoined(remoteUser, true);
              if (res instanceof Promise) {
                res.then(() => console.debug("Frame decode call finished"));
              }
              // const VideoOverlayConatiner = elem.children[0];
              // const buttonElement = document.getElementById("buttons-view");
              // if (buttonElement && buttonElement.style.display !== "")
              //   buttonElement.style.display = "";
              // const img = document.createElement("img");
              // img.style.position = "absolute";
              // img.style.height = "100%";
              // img.id = "phone-bg";
              // img.src = require("../../assets/TranspDevice_no_unclaibarte.png");
              // VideoOverlayConatiner.append(img);
              // const videoElemnt = VideoOverlayConatiner.children[0];
              // videoElemnt.className = `${videoElemnt.className} no-z`; //setting the video to have no z for future overlay
              // VideoOverlayConatiner.className = `${elem.children[0].className} no-bg no-pos Xoltar-video`;
            } catch (e) {
              console.debug(e);
            }
          });
        }
      }
      if (mediaType === "audio" && remoteUser.uid !== client.uid) {
        (track as IRemoteAudioTrack).play();
        (track as IRemoteAudioTrack).setAudioFrameCallback(() => {
          onVolumeUpdate &&
            onVolumeUpdate(
              track.getUserId(),
              (track as IRemoteAudioTrack).getVolumeLevel()
            );
        });
      }
    },
    // eslint-disable-next-line
    [client]
  );

  const onRemoteUserJoined = useCallback(
    async (remoteUser: IAgoraRTCRemoteUser) => {
      console.debug(`User with uid ${remoteUser.uid} joined`);
      const result = onRemoteJoined && onRemoteJoined(remoteUser, false);
      if (result instanceof Promise) {
        await result;
      }
      return;
    },
    [onRemoteJoined]
  );
  const onRemoteUserLeft = useCallback(
    async (remoteUser: IAgoraRTCRemoteUser) => {
      console.debug(`User with uid ${remoteUser.uid} left`);
      const promises: Promise<any>[] = [];
      if (`${remoteUser.uid}` === `${5}`) {
        client.localTracks.forEach((track) => {
          track.stop();
          track.close();
        });
        try {
          if (onCoreLeft) {
            const result = onCoreLeft();
            result instanceof Promise && promises.push(result);
          }
        } catch (error) {
          console.error(error);
        }

        client.removeAllListeners();
        promises.push(client.leave());
        const promisesResult = await Promise.allSettled(promises);
        promisesResult.forEach((res) => {
          res.status === "fulfilled"
            ? console.debug(res.value)
            : console.error(res.reason);
        });
      }
    },
    // eslint-disable-next-line
    []
  );
  const joinChannel = useCallback(
    async (camId: string, micId: string, localView?: HTMLDivElement | null) => {
      if (client.localTracks.length === 2 && client.uid && client.channelName)
        return [client.uid, client.channelName] as [UID, string];
      const videoTrack = await AgoraRTC.createCameraVideoTrack({
        cameraId: camId,
        encoderConfig: "480p_2",
      });
      const audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        microphoneId: micId,
      });
      if (localView && videoTrack) {
        videoTrack.play(localView, {});
      }
      const uid = await client.join(
        AGORA_APP_ID,
        channel,
        await getToken(channel)
      );
      await client.publish([videoTrack, audioTrack]);

      return [uid, channel] as [UID, string];
    },
    [client, channel]
  );
  const leaveChannel = useCallback(
    async () => {
      return await client.leave();
    },
    // eslint-disable-next-line
    [client, channel]
  );
  const toggleAudio = useCallback(async (value?: boolean) => {
    const audio = client.localTracks
      .filter((track) => track.trackMediaType === "audio")
      .at(0);
    if (value === undefined) audio && (await audio.setMuted(!audio.muted));
    else audio && (await audio.setMuted(value));
    // eslint-disable-next-line
  }, []);
  const toggleVideo = useCallback(
    async (value?: boolean) => {
      const video = client.localTracks
        .filter((track) => track.trackMediaType === "video")
        .at(0);
      if (value === undefined) video && (await video.setMuted(!video.muted));
      else video && (await video.setMuted(value));
    },
    // eslint-disable-next-line
    [client, channel]
  );

  useEffect(() => {
    const currentClient = client;
    client.on("user-published", (user, mediaType) => {
      if (user.uid === "5" || user.uid === 5)
        onRemoteUserPublished(user, mediaType);
      else console.warn("Unknown user", user);
    });
    currentClient.on("token-privilege-will-expire", async () => {
      await currentClient.renewToken(await getToken(channel));
    });
    currentClient.on("network-quality", (stat: NetworkQuality) => {
      const container = document.getElementsByTagName("video");
      for (let i = 0; i < container.length; i++) {
        const e = container.item(i);
        if (!e) continue;
        if (e.id.search("video-5-c")) {
          const img = document.getElementById("phone-img");
          if (img)
            img.style.width = `${e.getBoundingClientRect().width * 1.1}px`;
        }
      }
      console.debug(stat);
    });
    currentClient.on("user-joined", onRemoteUserJoined);
    currentClient.on("user-left", onRemoteUserLeft);
    currentClient.on("is-using-cloud-proxy", (is_proxy: boolean) => {
      console.warn("Using cloud-proxy", is_proxy);
    });
    currentClient.enableAudioVolumeIndicator();
    if (onVolumeUpdate) {
      currentClient.on("volume-indicator", (results) => {
        results.forEach((value) => {
          onVolumeUpdate(value.uid, value.level);
        });
      });
    }
    // eslint-disable-next-line
  }, [client]);

  return {
    joinChannel,
    leaveChannel,
    toggleAudio,
    toggleVideo,
    agoraClient: client,
  };
};
